export type SelectionState = "none" | "some" | "all";

/** Checked when every visible id is selected. Extra ids outside the list do not change the state. */
export function selectionState(
  visibleIds: readonly string[],
  selected: ReadonlySet<string>,
): SelectionState {
  if (visibleIds.length === 0) return "none";
  let matches = 0;
  for (const id of visibleIds) {
    if (selected.has(id)) matches += 1;
  }
  if (matches === 0) return "none";
  if (matches === visibleIds.length) return "all";
  return "some";
}

/**
 * Checking replaces the selection with the visible ids.
 * Unchecking removes only those ids.
 */
export function toggleVisibleSelection(
  visibleIds: readonly string[],
  selected: ReadonlySet<string>,
): Set<string> {
  if (selectionState(visibleIds, selected) === "all") {
    const next = new Set(selected);
    for (const id of visibleIds) next.delete(id);
    return next;
  }
  return new Set(visibleIds);
}

function normalizedZipPath(relativePath: string, fileId: string): string {
  const parts = relativePath
    .replace(/\\/g, "/")
    .split("/")
    .map((part) => part.trim())
    .filter((part) => part && part !== "." && part !== "..");
  const base = parts.join("/");
  if (base) return base;
  return `file-${fileId.replace(/-/g, "").slice(0, 8)}`;
}

/** Stable zip entry name. Collisions after sanitizing get a short id before the extension. */
export function allocateZipEntryName(
  relativePath: string,
  fileId: string,
  used: Set<string>,
): string {
  const base = normalizedZipPath(relativePath, fileId);
  const key = (value: string) => value.toLowerCase();
  if (!used.has(key(base))) {
    used.add(key(base));
    return base;
  }

  const slash = base.lastIndexOf("/");
  const dir = slash >= 0 ? base.slice(0, slash + 1) : "";
  const filePart = slash >= 0 ? base.slice(slash + 1) : base;
  const dot = filePart.lastIndexOf(".");
  const stem = dot > 0 ? filePart.slice(0, dot) : filePart;
  const ext = dot > 0 ? filePart.slice(dot) : "";
  const suffix = fileId.replace(/-/g, "").slice(0, 8) || "file";

  let candidate = `${dir}${stem} (${suffix})${ext}`;
  let n = 2;
  while (used.has(key(candidate))) {
    candidate = `${dir}${stem} (${suffix}-${n})${ext}`;
    n += 1;
  }
  used.add(key(candidate));
  return candidate;
}

export function zipDownloadFileName(folderNames: readonly string[]): string {
  const unique = [
    ...new Set(folderNames.map((name) => name.trim()).filter(Boolean)),
  ];
  const raw = unique.length === 1 ? unique[0] : "files";
  const ascii = raw
    .normalize("NFKD")
    .replace(/[^\x20-\x7E]/g, "")
    .replace(/["\\]/g, "")
    .replace(/[^\w. -]+/g, "-")
    .replace(/\s+/g, " ")
    .trim();
  return `${ascii || "files"}.zip`;
}
